import React, { FC, useEffect, useRef } from "react";
import { useCurrencySymbol } from "hooks/useCurrencySymbol";
import { ButtonColor } from "enums/Button";
import { IconType } from "enums/Icon";
import { useTranslation } from "react-i18next";
import { connect } from "react-redux";
import { createStructuredSelector } from "reselect";
import { OutgoingRowLayoutTier } from "types/outgoing";
import Icon from "../../../../components/Icon/Icon";
import useContainerQuery from "../../../../hooks/useContainerQuery";
import { seedDemoOutgoing } from "../../../../reduxFolder/reducers/outgoing";
import { authSelectUnits } from "../../../../reduxFolder/selectors/globalSelectors";
import {
  HeaderTitle,
  HeaderWrapper,
  ParcelsWrapper,
} from "../../../../styles/parcelsStyles";
import formatter from "../../../../utils/formatter";
import DemoFrame from "../../../Storage/components/DemoShipments/DemoFrame";
import { FadeWrapper } from "../../../Storage/components/DemoShipments/DemoShipments.styles";
import { useDemoHint } from "../../../Storage/components/DemoShipments/useDemoHint";
import {
  PayAllHeaderButton,
  PaymentHeaderContent,
} from "../../Outgoing.styles";
import InProgress from "../InProgress/InProgress";
import NotFinished from "../NotFinished/NotFinished";
import { getOutgoingLayoutTier } from "../rowLayout";
import WaitingForPayment from "../WaitingFor/WaitingForPayment";
import {
  demoDraftConsolidation,
  demoOutgoingSeedPayload,
  demoPackingItems,
  demoPaymentItems,
  demoPaymentTotal,
} from "./demoOutgoingFixtures";

interface DemoOutgoingProps {
  units: string;
  seedDemoOutgoing: (payload: any) => void;
}

const DemoOutgoing: FC<DemoOutgoingProps> = ({ units, seedDemoOutgoing }) => {
  const { t } = useTranslation("common");
  const currencySymbol = useCurrencySymbol();
  const showDemoHint = useDemoHint();
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  // Demo rows sit directly in the page (no CollapseWrapper inset), so they
  // measure their own wrapper, same as DemoShipments.
  const layoutTier = useContainerQuery<OutgoingRowLayoutTier>(
    wrapperRef,
    getOutgoingLayoutTier,
    "full",
  );

  // Pre-seed both outgoing detail caches before any row can be clicked, so
  // the panels render demo consolidations without ever fetching.
  useEffect(() => {
    seedDemoOutgoing(demoOutgoingSeedPayload);
  }, [seedDemoOutgoing]);

  return (
    <DemoFrame
      title={t("parcels.demo.outgoingTitle")}
      subtitle={t("parcels.demo.outgoingSubtitle")}
    >
      <FadeWrapper ref={wrapperRef}>
        <section>
          <HeaderWrapper>
            <HeaderTitle>
              <Icon type={IconType.Pause} color="#ffaf5d" />
              {t("parcels.notFinished")}
            </HeaderTitle>
          </HeaderWrapper>
          <ParcelsWrapper>
            <NotFinished
              units={units}
              parcel={demoDraftConsolidation}
              layoutTier={layoutTier}
              isDemo
            />
          </ParcelsWrapper>
        </section>
        <section>
          <HeaderWrapper>
            <HeaderTitle>
              <Icon type={IconType.Add} color="#3ebcf7" />
              {`${t("parcels.inProgress")}: ${demoPackingItems.size}`}
            </HeaderTitle>
          </HeaderWrapper>
          <ParcelsWrapper>
            {demoPackingItems.map((parcel: any) => (
              <InProgress
                key={parcel.get("id")}
                units={units}
                parcel={parcel}
                layoutTier={layoutTier}
                isDemo
              />
            ))}
          </ParcelsWrapper>
        </section>
        <section>
          <HeaderWrapper>
            <PaymentHeaderContent>
              <HeaderTitle>
                <Icon type={IconType.Money} color="#1fd983" />
                {`${t("parcels.waitingForPayment")}: ${demoPaymentItems.size}`}
              </HeaderTitle>
              <PayAllHeaderButton
                color={ButtonColor.Secondary}
                onClick={showDemoHint}
              >
                {`${t("shipping.total")}`}
                <b>{` ${currencySymbol}${formatter.currency(demoPaymentTotal)}`}</b>
              </PayAllHeaderButton>
            </PaymentHeaderContent>
          </HeaderWrapper>
          <ParcelsWrapper>
            {demoPaymentItems.map((parcel: any) => (
              <WaitingForPayment
                key={parcel.get("id")}
                units={units}
                parcel={parcel}
                layoutTier={layoutTier}
                isDemo
              />
            ))}
          </ParcelsWrapper>
        </section>
      </FadeWrapper>
    </DemoFrame>
  );
};

const withConnect = connect(
  createStructuredSelector({
    units: authSelectUnits,
  }),
  { seedDemoOutgoing },
);

export default withConnect(DemoOutgoing);
